/**
 * Offline answer queue — Spec Bölüm 7.3.2 (S2.T9)
 *
 * Aday sınav sırasında internet kesilirse cevaplar localStorage'a kuyruğa girer.
 * Bağlantı geri geldiğinde sırayla flush edilir.
 *
 * Neden localStorage? Cevap payload'ı çok küçük (questionId + key + timeSpent).
 * IndexedDB / LocalForage overkill. localStorage 5MB+, key-per-attempt yapısı yeterli.
 *
 * Idempotency: Backend `/answer` aynı (attemptId, questionId) için update yapıyor.
 * Aynı cevap iki kez gönderilse bile sorun yok.
 */
import { api, ApiError } from './api.js';

export interface QueuedAnswer {
  questionId: string;
  selectedOption?: string;
  numericAnswer?: number;
  timeSpentSec: number;
  enqueuedAt: number;
}

const KEY_PREFIX = 'swskills:offline-q:';

function key(attemptId: string): string {
  return `${KEY_PREFIX}${attemptId}`;
}

function readQueue(attemptId: string): QueuedAnswer[] {
  try {
    const raw = localStorage.getItem(key(attemptId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueuedAnswer[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(attemptId: string, items: QueuedAnswer[]): void {
  try {
    if (items.length === 0) {
      localStorage.removeItem(key(attemptId));
    } else {
      localStorage.setItem(key(attemptId), JSON.stringify(items));
    }
  } catch (err) {
    // Quota aşımı vs — sessizce yut (V1)
    console.error('Offline queue write failed', err);
  }
}

/**
 * Bir cevabı önce gönder; ağ hatasıysa kuyruğa al.
 *
 * Sunucu hatası (4xx) durumunda kuyruğa ALMAZ — kullanıcıya bildirilmesi gerekir.
 * Sadece network-level error'da queue kullanırız.
 */
export async function submitOrQueueAnswer(
  attemptId: string,
  answer: Omit<QueuedAnswer, 'enqueuedAt'>,
): Promise<{ status: 'sent' | 'queued'; queueLength: number }> {
  try {
    await api.post(`/api/public/attempts/${attemptId}/answer`, answer);
    return { status: 'sent', queueLength: readQueue(attemptId).length };
  } catch (err) {
    // Network error mı, server error mı?
    // ApiError → server response var, normal hata
    // Diğer → fetch reddi (offline/DNS/CORS) → queue
    if (err instanceof ApiError) {
      throw err;
    }
    const items = readQueue(attemptId);
    items.push({ ...answer, enqueuedAt: Date.now() });
    writeQueue(attemptId, items);
    return { status: 'queued', queueLength: items.length };
  }
}

/**
 * Kuyruktaki tüm cevapları sıraya göre flush et.
 * Başarılı olanları kaldır, hata olursa kalanları geride bırak.
 */
export async function flushQueue(
  attemptId: string,
): Promise<{ flushed: number; remaining: number }> {
  const items = readQueue(attemptId);
  if (items.length === 0) return { flushed: 0, remaining: 0 };

  let flushed = 0;
  const remaining: QueuedAnswer[] = [];
  for (const item of items) {
    try {
      await api.post(`/api/public/attempts/${attemptId}/answer`, {
        questionId: item.questionId,
        selectedOption: item.selectedOption,
        numericAnswer: item.numericAnswer,
        timeSpentSec: item.timeSpentSec,
      });
      flushed++;
    } catch (err) {
      if (err instanceof ApiError) {
        // Server reddetti — bu cevabı sil (ör. sınav süresi doldu)
        // Geri kalanlar denenmesin (CONFLICT/409 ise hepsi düşer)
        if (err.code === 'CONFLICT' || err.code === 'NOT_FOUND') {
          // Tüm queue'yu sil
          writeQueue(attemptId, []);
          throw err;
        }
        // 400 vs gerçek validation — kullanıcı görmeli
        throw err;
      }
      // Network hatası — kalan kuyruğu bırak
      remaining.push(item);
      // Sonraki itemleri de bırak (sıralı flush)
      const idx = items.indexOf(item);
      for (let i = idx + 1; i < items.length; i++) {
        remaining.push(items[i]!);
      }
      break;
    }
  }

  writeQueue(attemptId, remaining);
  return { flushed, remaining: remaining.length };
}

/** Kuyruk uzunluğu (UI için) */
export function getQueueLength(attemptId: string): number {
  return readQueue(attemptId).length;
}

/** Attempt tamamlandığında kuyruğu temizle */
export function clearQueue(attemptId: string): void {
  writeQueue(attemptId, []);
}
